/**
 * The Reveal.
 *
 * At Era open `GET /api/boot` publishes `SHA-256(ERA_SECRET)`. At Era close the secret itself is
 * published, and every roll of the twelve weeks becomes checkable against a hash that was public
 * before the first action of the Era. `docs/DESIGN-DELTAS.md` 1k.14 called the version of this
 * that existed in July "a cryptographic commitment the game publishes and cannot honour"; the
 * keystream that made it honourable shipped on 2026-07-31, and this screen is the half a PLAYER
 * can reach.
 *
 * **The check runs in the browser, against the browser's own crypto.** Not on the server, and
 * not as a green tick the server hands down. `crypto.subtle` computes the HMAC, this file walks
 * the words out of it, and the comparison is against numbers the server wrote before the secret
 * existed publicly. A verification a player has to take the server's word for is not one.
 *
 * The rows are published ALL ERA, twelve weeks before the key. That ordering is the whole point:
 * a list handed over at the same instant as the key proves nothing about either, and a list that
 * has been sitting in front of you since week one proves that nothing was chosen after the fact.
 */

import { useCallback, useEffect, useMemo, useState } from 'react'
import type { RevealPayload, RevealRow } from '@ashport/shared'
import { Btn, Col, Panel, PanelHead, Row, Spacer } from '../ui/Kit'
import { api } from '../net/api'
import { sz } from '../ui/viewport'

type Verdict = 'unchecked' | 'checking' | 'match' | 'mismatch' | 'off-stream'

/**
 * `HMAC-SHA256(secret, "roll:e{era}:{playerId}:{block}")`, as eight big-endian uint32 words.
 *
 * A deliberate re-implementation of `server/src/lib/roll.ts` rather than an import of it. That
 * file lives in `server/` and the client cannot reach it, and it should not: the point of a
 * verification is that a second, independent implementation of the published recipe agrees. If
 * these two ever disagree the recipe has changed, which is exactly what a player would want to
 * find out.
 */
async function blockWords(secret: string, label: string): Promise<number[]> {
  const enc = new TextEncoder()
  const key = await crypto.subtle.importKey(
    'raw',
    enc.encode(secret),
    { name: 'HMAC', hash: 'SHA-256' },
    false,
    ['sign'],
  )
  const mac = new Uint8Array(await crypto.subtle.sign('HMAC', key, enc.encode(label)))
  const out: number[] = []
  for (let i = 0; i < 8; i++) {
    const o = i * 4
    // `>>> 0` because the shift operators are signed and a word with the high bit set would
    // otherwise come back negative. The server's `rollBlock` says the same thing.
    out.push(((mac[o] << 24) | (mac[o + 1] << 16) | (mac[o + 2] << 8) | mac[o + 3]) >>> 0)
  }
  return out
}

async function checkRow(
  secret: string,
  era: number,
  playerId: string,
  wordsPerBlock: number,
  row: RevealRow,
): Promise<Verdict> {
  if (row.rollWord < 0) return 'off-stream'
  if (row.words.length === 0) return 'mismatch'
  const cache = new Map<number, number[]>()
  for (let i = 0; i < row.words.length; i++) {
    const n = row.rollWord + i
    const block = Math.floor(n / wordsPerBlock)
    let words = cache.get(block)
    if (!words) {
      words = await blockWords(secret, `roll:e${era}:${playerId}:${block}`)
      cache.set(block, words)
    }
    if (words[n % wordsPerBlock] !== row.words[i]) return 'mismatch'
  }
  return 'match'
}

const VERDICT_LABEL: Record<Verdict, string> = {
  unchecked: '',
  checking: 'checking',
  match: 'checks out',
  mismatch: 'DOES NOT MATCH',
  'off-stream': 'no roll',
}

const VERDICT_COLOUR: Record<Verdict, string> = {
  unchecked: 'var(--ink-dim)',
  checking: 'var(--ink-mid)',
  match: 'var(--r-uncommon)',
  mismatch: 'var(--blood)',
  'off-stream': 'var(--ink-dim)',
}

export function Reveal({ compact = false }: { compact?: boolean }) {
  const [data, setData] = useState<RevealPayload | null>(null)
  const [fault, setFault] = useState<string | null>(null)
  const [verdicts, setVerdicts] = useState<Record<string, Verdict>>({})
  const [ran, setRan] = useState(false)

  useEffect(() => {
    let live = true
    api
      .reveal()
      .then((d) => {
        if (live) setData(d)
      })
      .catch((e: unknown) => {
        if (live) setFault(e instanceof Error ? e.message : 'The line went dead.')
      })
    return () => {
      live = false
    }
  }, [])

  const check = useCallback(async () => {
    if (!data?.secret) return
    setRan(true)
    setVerdicts(Object.fromEntries(data.rows.map((r) => [r.id, 'checking' as Verdict])))
    /*
     * Sequential, not `Promise.all`. Every row is one or two `crypto.subtle.sign` calls and two
     * hundred of them fired at once locks the main thread for long enough that the "checking"
     * state never paints, which makes a real computation look like a fake one.
     */
    for (const row of data.rows) {
      const v = await checkRow(data.secret, data.era, data.playerId, data.wordsPerBlock, row)
      setVerdicts((prev) => ({ ...prev, [row.id]: v }))
    }
  }, [data])

  const tally = useMemo(() => {
    const values = Object.values(verdicts)
    return {
      match: values.filter((v) => v === 'match').length,
      mismatch: values.filter((v) => v === 'mismatch').length,
      offStream: values.filter((v) => v === 'off-stream').length,
      pending: values.filter((v) => v === 'checking').length,
    }
  }, [verdicts])

  if (fault !== null) {
    return (
      <Panel style={{ flex: 1, minHeight: 0 }}>
        <PanelHead title="The Reveal" icon="ledger" sub="Every roll of the Era, checkable" />
        <div style={{ padding: 16, color: 'var(--ink-mid)', fontSize: 13 }}>{fault}</div>
      </Panel>
    )
  }

  if (data === null) {
    return (
      <Panel style={{ flex: 1, minHeight: 0 }}>
        <PanelHead title="The Reveal" icon="ledger" sub="Every roll of the Era, checkable" />
        <div style={{ padding: 16, color: 'var(--ink-dim)', fontSize: 13 }}>Pulling the file.</div>
      </Panel>
    )
  }

  const sealed = data.secret === null

  return (
    <Col gap={8} style={{ flex: 1, minHeight: 0 }}>
      <Panel style={{ flex: 'none' }}>
        <PanelHead
          title="The Reveal"
          icon="ledger"
          sub={`Era ${data.era}, ${data.eraName}`}
          accent="var(--gold)"
        />
        <div style={{ padding: '10px 12px 12px' }}>
          <p style={{ margin: 0, fontSize: 12.5, color: 'var(--ink-mid)', lineHeight: 1.5 }}>
            {sealed
              ? 'The house published the hash of this Era’s key before the first job of the Era was taken, and the key itself lands when the books close. Until then, every roll you have made is listed below with the exact position in the sequence it came from. Nothing here can be changed afterwards, which is the only reason checking it later means anything.'
              : 'The key is out. Every line below was rolled from it, and your browser can prove that without asking us: the check runs here, against numbers we wrote down weeks before you could read the key.'}
          </p>
          <div style={{ marginTop: 10, display: 'grid', gap: 4 }}>
            <Field label="Recipe" value={data.recipe} />
            <Field label="Hash, published at Era open" value={data.secretHash} />
            <Field label="Key" value={data.secret ?? 'sealed until the books close'} />
            <Field label="Your id" value={data.playerId} />
            {/*
             * THE TIER MODIFIER, ON THE REPLAY. GAME-DESIGN 13.3 constraint 3: "the replay tool
             * shows exactly what happened, including the tier modifier. The game does not lie
             * about its own dice."
             *
             * Shown at tier 0 too, where the multiplier is exactly 1.0. A row that appears only
             * when there is something to disclose is a row whose presence IS the disclosure, and
             * a player who never sees it cannot know it would have been there.
             *
             * It says explicitly that the dice are untouched, because that is the actual fact and
             * it is the one a player disputing a payout most needs: the multiplier lands after
             * the outcome, so every roll below is the roll the server used.
             */}
            <Field
              label="Yield modifier"
              value={
                data.shadowTier === 0
                  ? 'None. Full rate, 1.00x'
                  : `Reduced rate, ${data.shadowYield.toFixed(2)}x on payouts. The rolls below are untouched.`
              }
            />
          </div>
          <Row gap={10} style={{ marginTop: 12, alignItems: 'center' }}>
            <Btn onClick={() => void check()} disabled={sealed || tally.pending > 0}>
              {tally.pending > 0 ? 'Checking' : 'Check every line'}
            </Btn>
            <Spacer />
            {ran && (
              <div style={{ fontSize: 12, color: 'var(--ink-mid)' }}>
                <span style={{ color: VERDICT_COLOUR.match }}>{tally.match} check out</span>
                {tally.mismatch > 0 && (
                  <span style={{ color: VERDICT_COLOUR.mismatch }}>, {tally.mismatch} do not</span>
                )}
                {tally.offStream > 0 && <span>, {tally.offStream} drew no roll</span>}
              </div>
            )}
          </Row>
        </div>
      </Panel>

      <Panel style={{ flex: 1, minHeight: 0 }}>
        <PanelHead
          title="Your rolls"
          icon="ledger"
          sub={`${data.rows.length} of ${data.total} actions`}
        />
        <div style={{ overflow: 'auto', minHeight: 0, maxHeight: compact ? 300 : undefined }}>
          <table className="grid-tbl">
            <thead>
              <tr>
                <th>When</th>
                <th>What</th>
                <th>Outcome</th>
                <th>Word</th>
                <th>Drew</th>
                <th>Check</th>
              </tr>
            </thead>
            <tbody>
              {data.rows.map((r) => {
                const v = verdicts[r.id] ?? 'unchecked'
                return (
                  <tr key={r.id}>
                    <td style={{ whiteSpace: 'nowrap' }}>
                      {new Date(r.at).toISOString().slice(0, 16).replace('T', ' ')}
                    </td>
                    <td>{r.refId || r.kind}</td>
                    <td>{r.outcome}</td>
                    <td style={{ textAlign: 'right' }}>{r.rollWord < 0 ? 'none' : r.rollWord}</td>
                    <td
                      style={{
                        fontFamily: 'var(--mono, monospace)',
                        fontSize: 11,
                        color: 'var(--ink-dim)',
                      }}
                    >
                      {r.words.join(' ')}
                    </td>
                    <td style={{ color: VERDICT_COLOUR[v], whiteSpace: 'nowrap' }}>
                      {VERDICT_LABEL[v]}
                    </td>
                  </tr>
                )
              })}
              {data.rows.length === 0 && (
                <tr>
                  <td colSpan={6} style={{ color: 'var(--ink-dim)', padding: 14 }}>
                    {sealed
                      ? 'Nothing on the sheet yet. Take a job.'
                      : 'You never took a swing this Era. There is nothing to check.'}
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </Panel>
    </Col>
  )
}

function Field({ label, value }: { label: string; value: string }) {
  return (
    <Row gap={8} style={{ alignItems: 'baseline' }}>
      <div className="t-label" style={{ fontSize: sz(8.5), width: 190, flex: 'none' }}>
        {label}
      </div>
      <div
        style={{
          fontFamily: 'var(--mono, monospace)',
          fontSize: 11,
          color: 'var(--ink-mid)',
          wordBreak: 'break-all',
          minWidth: 0,
        }}
      >
        {value}
      </div>
    </Row>
  )
}
